import { Fragment, type ReactElement, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  ModelledTag,
  Money,
  SectionHeading,
  ShareBar,
  SubHeading,
  Tile,
  plural,
} from "../components/kit";
import { formatCents } from "@shared/money";

/**
 * The role-aware landing screen.
 *
 * Every number here is live from Convex. The reactive query means a manager
 * watching this screen sees the count move the moment an assistant submits a
 * request — which is the single most persuasive thing in the demo and costs
 * nothing extra.
 *
 * **This is the projector screen**, so the tiles are grouped rather than poured
 * into one undifferentiated grid: what needs doing, what is in flight, what is
 * owed. Someone reading it from the back of a room should be able to answer
 * "is anything on fire?" without reading a single label — which is also why
 * red is spent carefully (see below).
 *
 * The grid is one grid with full-width headings inside it rather than three
 * grids stacked, so every tile is the same width no matter which group it is
 * in and a group that a role cannot see simply disappears without leaving a
 * ragged column behind.
 *
 * **The by-clinic table underneath is the first question anyone asks.** "Eighty
 * open requests" is followed immediately by "at which clinic?", and a dashboard
 * that cannot answer that sends the room to a different screen. Every figure in
 * it comes from a query this app already had — the outstanding-requests report,
 * the alert list and the unpaid invoices — so nothing new was asked of the
 * server to answer it.
 */

/** A group with no tiles for this role is dropped, heading and all. */
type Group = { key: string; heading: string; tiles: ReactElement[] };

/** One clinic's line in the breakdown. Assembled on the client from three
 *  existing queries, keyed by name because the clinics that have *nothing*
 *  outstanding come back from the report as names only. */
type ClinicLine = {
  name: string;
  code: string | null;
  openLines: number;
  urgentLines: number;
  alerts: number;
  outstandingCents: number;
  overdueInvoices: number;
};

/**
 * The breakdown's heading, in one place.
 *
 * It is needed three times — on `ScreenBoundary`'s error path, on the
 * skeleton, and on the table itself — and three copies of the same JSX is three
 * chances for the projector screen to grow a heading that reads one size on the
 * good path and another when a query threw. It is a plain `SubHeading` now: the
 * child span that used to re-set the size and colour was fighting the kit's own
 * typography, which is already `text-sm text-muted-foreground`.
 */
const BY_CLINIC_HEADING = <SubHeading className="mb-2">By clinic</SubHeading>;

export function Overview() {
  const summary = useQuery(api.dashboard.summary);
  /**
   * "Inventory value" on this screen is derived from `nexoraCost`, and 85 of
   * the 97 items have no supplier cost on file — the figure is modelled from
   * the client's own stated discount band, not quoted.
   *
   * This is the screen that goes on a projector, so it is the likeliest place
   * for a modelled number to be read out as though it were real. Every other
   * surface that shows a cost-derived figure carries this statement; the
   * dashboard was the one that did not.
   *
   * `"skip"` unless the tile is actually shown: the query is owner-only, and a
   * manager loading this screen would otherwise be refused for a figure that
   * is not on her version of it.
   */
  const costBasis = useQuery(
    api.pricing.costBasisSummary,
    summary?.inventoryValueCents != null ? {} : "skip",
  );

  /** The unpaid invoices, for the "of which overdue" split on the Outstanding
   *  tile. Gated on the same field the tile is gated on, so a role never asks
   *  the server for a figure its own summary already declined to return. */
  const unpaid = useQuery(
    api.invoices.list,
    summary?.outstandingCents != null ? { unpaidOnly: true } : "skip",
  );

  /**
   * How many clinics have submitted this week's count.
   *
   * v2 inverts the model — the weekly count is what keeps the clinics stocked —
   * and the client's chronic failure is enforcement, so "4 of 5" is the figure
   * that says whether the new loop is actually running. `{ weeks: 1 }` because
   * the dashboard only asks about this week; the history lives on /compliance.
   *
   * `"skip"` unless the viewer is an owner: `counts.compliance` is owner-only
   * and server-gated, so any other role loading this screen would be refused
   * for a figure that is not on their version of it.
   */
  const compliance = useQuery(
    api.counts.compliance,
    summary?.role === "owner" ? { weeks: 1 } : "skip",
  );

  if (summary === undefined)
    return <Loading rows={4} label="Loading overview" />;

  const nothingOutstanding =
    summary.openRequestLines === 0 &&
    summary.backOrderedLines === 0 &&
    summary.awaitingReceipt === 0;

  /* Overdue is free: the unpaid-invoice list already carries the server's
   * derived status per invoice, so the split costs a filter rather than a query.
   *
   * `status === "overdue"`, and not `daysOverdue > 0`, because the two disagree
   * for the first day past due — `daysOverdue` floors to 0 while the status has
   * already flipped. This tile and the Invoices screen are read side by side in
   * the same demo, so they use the one predicate. */
  const overdueInvoices = (unpaid ?? []).filter(
    (invoice) => invoice.status === "overdue",
  );
  const overdueCents = overdueInvoices.reduce(
    (total, invoice) => total + invoice.outstandingCents,
    0,
  );

  /* ---- The three groups ------------------------------------------------ */

  /**
   * Needs action: things that stop moving until a person does something.
   *
   * Tone is spent, not sprayed. Red means *go and do this now* — below the
   * minimum, an unacknowledged alert. "80 open requests" is a Monday, not an
   * emergency, so it stays neutral however large it gets; a dashboard where
   * every number is red is a dashboard nobody reads twice.
   */
  const needsAction: ReactElement[] = [];
  if (summary.lowStockItems !== null) {
    needsAction.push(
      <Tile
        key="low"
        label="Below minimum"
        value={summary.lowStockItems}
        hint="Warehouse stock · opens Inventory"
        size="sm"
        tone={summary.lowStockItems > 0 ? "red" : "green"}
        to="/inventory"
      />,
    );
  }
  if (summary.unacknowledgedAlerts !== null) {
    needsAction.push(
      <Tile
        key="alerts"
        label="Alerts"
        value={summary.unacknowledgedAlerts}
        hint="Not yet acknowledged · opens Alerts"
        size="sm"
        tone={summary.unacknowledgedAlerts > 0 ? "red" : "neutral"}
        to="/alerts"
      />,
    );
  }
  /* A clinic that has not counted this week is someone to chase — but the
     current week is *in progress*, so an outstanding clinic is not yet a
     failure. Neutral, not red, until the week is actually over: red that fires
     every Monday morning is red the owner learns to ignore, and the Tile has no
     amber to spend. Only `counted` clears the tile to green — the loop is
     "running" once everyone due is in. Pushed once the query has landed, the
     same way the money tiles wait for theirs. */
  if (compliance !== undefined) {
    const counted = compliance.clinics.filter((clinic) =>
      clinic.weeks.some(
        (week) =>
          week.weekKey === compliance.currentWeekKey &&
          week.state === "counted",
      ),
    ).length;
    const allIn = counted >= compliance.clinics.length;
    needsAction.push(
      <Tile
        key="counts"
        label="Counts this week"
        value={`${counted}/${compliance.clinics.length}`}
        hint="Clinics that have counted · opens Counts"
        size="sm"
        tone={allIn ? "green" : "neutral"}
        to="/compliance"
      />,
    );
  }

  /** In flight: waiting on a vendor or on a courier. Nobody here is blocked on
   *  a decision, so nothing in this group is ever red. */
  const inFlight: ReactElement[] = [
    <Tile
      key="back"
      label="Back ordered"
      value={summary.backOrderedLines}
      hint="With the vendor · opens Requests"
      size="sm"
      to="/requests"
    />,
    <Tile
      key="await"
      label="Awaiting receipt"
      value={summary.awaitingReceipt}
      hint="Dispatched, not confirmed · opens Receiving"
      size="sm"
      to="/receive"
    />,
  ];

  /** Money. Owner only — the server omits these fields for every other role,
   *  so the group vanishes rather than being hidden. */
  const money: ReactElement[] = [];
  if (summary.outstandingCents !== null) {
    const unpaidCount = summary.outstandingInvoices ?? 0;
    money.push(
      <Tile
        key="outstanding"
        label="Outstanding"
        value={formatCents(summary.outstandingCents)}
        /* "Of which overdue" is the half of this figure that is a problem, and
           it costs nothing to say — the unpaid list is already on the client. */
        hint={
          overdueInvoices.length > 0
            ? `${unpaidCount} unpaid · ${overdueInvoices.length} overdue (${formatCents(
                overdueCents,
              )}) · opens Invoices`
            : `${unpaidCount} unpaid ${plural(unpaidCount, "invoice")} · opens Invoices`
        }
        size="sm"
        to="/invoices"
      />,
    );
  }
  if (summary.inventoryValueCents !== null) {
    money.push(
      <Tile
        key="inventory"
        label="Inventory value"
        value={formatCents(summary.inventoryValueCents)}
        hint="Warehouse, at modelled cost · opens Reports"
        size="sm"
        to="/reports"
      />,
    );
  }

  /* "Needs action" is deliberately not a group any more: its tiles sit in the
     hero's own row, beside it. See the grid below. */
  const groups: Group[] = [
    { key: "flight", heading: "In flight", tiles: inFlight },
    { key: "money", heading: "Money", tiles: money },
  ].filter((group) => group.tiles.length > 0);

  return (
    <div className="space-y-6">
      <SectionHeading
        title={summary.clinicName ?? "All clinics"}
        subtitle={
          summary.clinicName
            ? "Your clinic right now"
            : "Across Dentudio, Lux Dental, True North, W68 and Cambie"
        }
        /* The room needs to know these are not last night's numbers. Static —
           a pulsing dot would be motion this app has not earned.
         *
         * Stock `<Badge variant="outline">`, which is what the shadcn dashboard
         * puts in this corner. Not `<Pill>` from kit: a pill's tone is one of
         * the four *status* tones, and "Live" is not a status. The dot is the
         * one colour on it, and it is `--primary` — interaction and liveness,
         * never state. */
        action={
          <Badge variant="outline" className="shrink-0 gap-1.5">
            <span aria-hidden className="size-1.5 rounded-full bg-primary" />
            Live
          </Badge>
        }
      />

      {nothingOutstanding ? (
        <Empty
          tone="good"
          title="Nothing outstanding"
          body="No open requests, no back orders, and every delivery has been confirmed."
        />
      ) : null}

      {/* One grid, and the hero lives *in* it.
       *
       * The lead number and the two things that need acting on are one row —
       * hero across two of the four desktop columns, "Below minimum" and
       * "Alerts" filling the other two. The hero used to be a block of its own
       * above the grid, which at 1280px drew a 1200px-wide card holding one
       * two-digit number and read as an unfinished panel. Half the row, with
       * the action tiles beside it, is the same prominence and no dead space.
       *
       * At 390px nothing changes: the grid is two columns, the hero takes both,
       * and the two action tiles sit under it side by side. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        {/* The one number this screen leads with.
         *
         * Three flat rows of near-identical tiles meant nothing led, so from
         * the back of a room the screen said only "here are some numbers".
         * Open requests is the figure the whole product is about — every role
         * can see it, it is what the clinics are waiting on, and it is the one
         * that answers "is anybody blocked right now". Everything else is a
         * consequence of it and is demoted to `sm`.
         *
         * Still neutral however large it gets: red on this app means *go and do
         * this now*, and eighty open requests is a Monday. */}
        <div className="col-span-2">
          <Tile
            size="hero"
            label={
              summary.clinicName
                ? "Open requests for your clinic"
                : "Open requests"
            }
            value={summary.openRequestLines}
            hint={
              summary.openRequestLines === 0
                ? "Nothing waiting on a decision · opens Requests"
                : "Requested, approved or back ordered · opens Requests"
            }
            to="/requests"
          />
        </div>
        {needsAction}
        {groups.map((group) => (
          <Fragment key={group.key}>
            {/* Plain `SubHeading` — the kit's own `text-sm` /
                `text-muted-foreground`. The child span that used to re-set both
                existed to beat an older micro-caps heading; there is nothing
                left to beat. */}
            <SubHeading className="col-span-full mt-4 first:mt-0">
              {group.heading}
            </SubHeading>
            {group.tiles}
          </Fragment>
        ))}
      </div>

      {/* Under the tiles, not in a footnote: this is the projector screen, and
          "inventory value" is the number most likely to be read out as though
          a supplier had quoted it. */}
      {summary.inventoryValueCents !== null &&
      (costBasis?.modelled ?? 0) > 0 ? (
        <ModelledTag />
      ) : null}

      {/* Its own boundary, and its own queries inside it.
       *
       * Three more live queries on the screen that goes on a projector is three
       * more ways for a thrown query to blank the whole dashboard, because
       * Convex throws during render and this route has no boundary of its own.
       * A failure here costs the breakdown and nothing else. */}
      {summary.role === "owner" ? (
        <ScreenBoundary
          title="The clinic breakdown could not load"
          heading={BY_CLINIC_HEADING}
          wrap={(body: ReactNode) => <section>{body}</section>}
        >
          <ByClinic />
        </ScreenBoundary>
      ) : null}
    </div>
  );
}

/**
 * Which clinic is producing the eighty open requests.
 *
 * The first question anyone asks of the headline figure, and until this table
 * the dashboard sent them to a different screen to answer it. Nothing new was
 * asked of the server: the outstanding-requests report already aggregates open
 * lines per clinic, the alert list already names each alert's clinic, and the
 * unpaid-invoice list already carries `daysOverdue`. This joins them by name —
 * by name, because the clinics with *nothing* outstanding come back from the
 * report as names only, and they belong on the table too: a clinic reading zero
 * is the good news.
 */
function ByClinic() {
  const perClinic = useQuery(api.reports.outstandingRequests.data, {});
  const alerts = useQuery(api.alerts.list, {});
  // Same arguments as the parent's call, so Convex serves both from one
  // subscription rather than reading the invoices twice.
  const unpaid = useQuery(api.invoices.list, { unpaidOnly: true });

  /* The heading is rendered here rather than by the parent so it appears exactly
     once: `ScreenBoundary` renders its `heading` only on the error path. */
  if (perClinic === undefined) {
    return (
      <section>
        {BY_CLINIC_HEADING}
        {/* A skeleton rather than nothing: `aria-busy` is what tells the screen
            capture that this section has not arrived yet, and an empty gap on a
            projector reads as a feature that does not work. */}
        <Loading rows={2} label="Loading the clinic breakdown" />
      </section>
    );
  }

  /* One map over both halves of the report, not two object literals repeating
     each other. The clinics with nothing outstanding come back as bare names,
     so every field except the name starts at nothing and the two things the
     report does know — the code and the line counts — are written over the top.
     Kept as two literals, a field added to one of them would have been missing
     from half the table with nothing failing. */
  const NOTHING_OUTSTANDING = {
    code: null as string | null,
    openLines: 0,
    urgentLines: 0,
    alerts: 0,
    outstandingCents: 0,
    overdueInvoices: 0,
  };
  const rows: ClinicLine[] = [
    ...perClinic.clinics,
    ...perClinic.clearClinics,
  ].map((entry) =>
    typeof entry === "string"
      ? { ...NOTHING_OUTSTANDING, name: entry }
      : {
          ...NOTHING_OUTSTANDING,
          name: entry.clinicName,
          code: entry.clinicCode as string | null,
          openLines: entry.openLines,
          urgentLines: entry.urgentLines,
        },
  );

  /** Alerts that name no clinic — warehouse low stock is org-wide — are counted
   *  under the table rather than silently dropped or misattributed. */
  let unattributedAlerts = 0;
  const index = new Map(rows.map((row) => [row.name, row]));
  for (const alert of alerts ?? []) {
    const row = alert.clinicName ? index.get(alert.clinicName) : undefined;
    if (row) row.alerts += 1;
    else unattributedAlerts += 1;
  }
  for (const invoice of unpaid ?? []) {
    const row = index.get(invoice.clinicName);
    if (!row) continue;
    row.outstandingCents += invoice.outstandingCents;
    // The same predicate as the Outstanding tile above and the Invoices screen.
    if (invoice.status === "overdue") row.overdueInvoices += 1;
  }

  /* Busiest first, and a bar for the share of open lines.
   *
   * At 390px this table was five near-identical paragraphs of small text, and
   * "which clinic is producing the eighty" — the question the table exists to
   * answer — needed reading rather than glancing. Ordering it and drawing one
   * bar per row makes the answer the shape of the block rather than a number to
   * be compared by eye. Alphabetical order carried no information; the clinics
   * with nothing outstanding now fall to the bottom, where "zero" is the good
   * news it was always meant to be. */
  rows.sort((a, b) =>
    b.openLines !== a.openLines
      ? b.openLines - a.openLines
      : a.name.localeCompare(b.name),
  );
  const busiest = Math.max(1, ...rows.map((row) => row.openLines));

  return (
    <section>
      {BY_CLINIC_HEADING}
      {/* Stock `<Card>`, emptied of its own padding and gap so the rows own the
          geometry: the registry card is built around header/content slots this
          list does not use. */}
      <Card className="gap-0 overflow-hidden p-0">
        {rows.map((row) => (
          <div
            key={row.name}
            className="border-b p-3 last:border-b-0 lg:grid lg:grid-cols-[minmax(0,1fr)_9rem_7rem_15rem] lg:items-center lg:gap-x-4 lg:px-4 lg:py-2.5"
          >
            <div className="min-w-0">
              <div className="flex min-w-0 items-baseline gap-2">
                <span className="truncate text-sm font-semibold">
                  {row.name}
                </span>
                {row.code ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {row.code}
                  </span>
                ) : null}
              </div>
              <ShareBar
                className="mt-1.5 lg:mt-1"
                size="sm"
                pct={Math.round((row.openLines / busiest) * 100)}
                minPct={row.openLines > 0 ? 4 : 0}
                role="img"
                label={`${row.name}: ${row.openLines} open ${plural(row.openLines, "line")}`}
              />
            </div>

            {/* One wrapping line of figures on a phone; three aligned
                    columns at `lg`, via `contents`. Each figure keeps its own
                    word, so the desktop table needs no header row and the phone
                    layout needs no explanation. */}
            <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-sm lg:contents">
              <span className="tabular-nums">
                <b
                  className={
                    row.openLines > 0
                      ? "text-foreground"
                      : "font-semibold text-muted-foreground"
                  }
                >
                  {row.openLines}
                </b>{" "}
                <span className="text-muted-foreground">open</span>
                {row.urgentLines > 0 ? (
                  <span className="text-red-700">
                    {" "}
                    · {row.urgentLines} urgent
                  </span>
                ) : null}
              </span>

              <span className="tabular-nums">
                <b
                  className={
                    row.alerts > 0 ? "text-red-700" : "text-muted-foreground"
                  }
                >
                  {row.alerts}
                </b>{" "}
                <span className="text-muted-foreground">
                  {plural(row.alerts, "alert")}
                </span>
              </span>

              <span className="text-foreground">
                <Money
                  cents={row.outstandingCents}
                  className={
                    row.outstandingCents > 0
                      ? "font-semibold"
                      : "text-muted-foreground"
                  }
                />{" "}
                <span className="text-muted-foreground">outstanding</span>
                {row.overdueInvoices > 0 ? (
                  <span className="text-red-700">
                    {" "}
                    · {row.overdueInvoices} overdue
                  </span>
                ) : null}
              </span>
            </div>
          </div>
        ))}
      </Card>

      {unattributedAlerts > 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {unattributedAlerts} further {plural(unattributedAlerts, "alert")} are
          warehouse-wide and belong to no single clinic.
        </p>
      ) : null}
    </section>
  );
}
